/**
 * ResearchShareModal — share a research chat with colleagues.
 *
 * Deliberately NOT a new sharing UI. It renders the platform's
 * `CollaboratorSelector` — the same control matters, tasks, files and tables
 * share — and supplies only the research persistence and permission rules
 * behind it.
 *
 * The collaborator endpoint returns its own shape:
 *
 *   { collaborator_id, name, email, initials, access, role }
 *
 * `collaborator_id` is the identity — NOT `collaborator_uuid`/`user_id`, which
 * is what the shared control reads. Passing the raw rows straight through is
 * why the owner rendered as "undefined": the control looked the person up by
 * `String(undefined)`, found nobody, and had no name to show. `normalise`
 * below is that fix.
 *
 * Permission rules, enforced in the UI as well as the backend:
 *   • The owner (`role === 'creator'`) is shown as Owner, locked — no access
 *     dropdown, no remove.
 *   • Nobody can change their OWN access, ever.
 *   • Only an admin can change or remove anyone else; everyone else sees the
 *     list read-only.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import { hideModal } from '../../store/actions/modalActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import {
  researchAddCollaborator,
  researchListCollaborators,
  researchRemoveCollaborator,
  researchUpdateCollaboratorAccess,
} from '../../store/actions/ChatAction';
import CollaboratorSelector from '../shared/CollaboratorSelector';
import Button from '../shared/TailwindComponents/Button';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import ProfileImage from '../Header/ProfileImage';
// Aliased: this module exports its own `normalise` (endpoint shape → the shape
// CollaboratorSelector reads), which is a different job from resolving one
// person's picture/name/initials out of whatever keys the row happens to use.
import { normalise as normaliseAccessPerson } from '../shared/access/accessModel';

type Access = 'view' | 'edit' | 'admin';

export interface ResearchCollaborator {
  collaborator_uuid?: string;
  user_id?: string | number;
  user_uuid?: string;
  name?: string;
  email?: string;
  initials?: string;
  access: Access;
  /** 'creator' marks the owner. */
  role?: string;
  [key: string]: any;
}

/** The identity the research endpoints key on. */
export function collaboratorKey(collaborator: ResearchCollaborator): string {
  return String(
    collaborator.collaborator_uuid ||
      collaborator.collaborator_id ||
      collaborator.user_uuid ||
      collaborator.user_id ||
      ''
  );
}

/** Is this row the chat's owner? */
export function isOwner(collaborator: ResearchCollaborator): boolean {
  return String(collaborator.role || '').toLowerCase() === 'creator';
}

/**
 * The endpoint's shape → the shape `CollaboratorSelector` reads.
 *
 * `collaborator_id` becomes `collaborator_uuid` (the control's identity key)
 * and the display fields are carried across so a row can render its own name
 * without depending on the org-people lookup resolving.
 */
export function normalise(rows: any[]): ResearchCollaborator[] {
  return (rows || []).map((row) => ({
    ...row,
    collaborator_uuid: String(
      row?.collaborator_uuid || row?.collaborator_id || row?.user_uuid || row?.user_id || ''
    ),
    name: row?.name || row?.full_name || row?.email || '',
    email: row?.email || '',
    initials: row?.initials || '',
    access: (row?.access as Access) || 'view',
    role: row?.role,
  }));
}

/**
 * Is this a uuid?
 *
 * The collaborator rows key on `collaborator_id`, which IS a user uuid. The
 * profile object stores that uuid under a key that has varied (`uuid`,
 * `user_uuid`, and `id` — where `/user-profile/me` puts the uuid). `user_id`
 * is an int and can NEVER match, so identifying the field by SHAPE is more
 * durable than guessing its name.
 */
export function isUuid(value: unknown): boolean {
  return typeof value === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
}

/** The current user's uuid, from whichever profile field actually holds one. */
export function currentUserUuid(userInfo: any): string {
  const candidate = [
    userInfo?.uuid,
    userInfo?.user_uuid,
    userInfo?.id,
    userInfo?.user?.uuid,
  ].find(isUuid);
  return candidate ? String(candidate).trim() : '';
}

/**
 * Find the current user's row.
 *
 * Matches on uuid first, then on email — a second, independent key, so a
 * profile shape we have not seen still resolves rather than silently locking
 * the owner out of their own sharing controls.
 */
export function findSelf(
  collaborators: ResearchCollaborator[],
  uuid: string,
  email?: string
): ResearchCollaborator | undefined {
  const byUuid = uuid
    ? collaborators.find((c) => collaboratorKey(c).toLowerCase() === uuid.toLowerCase())
    : undefined;
  if (byUuid) return byUuid;
  const needle = String(email || '').trim().toLowerCase();
  if (!needle) return undefined;
  return collaborators.find((c) => String(c.email || '').trim().toLowerCase() === needle);
}

/** May this user change anyone's access on this chat? */
export function canManageAccess(
  collaborators: ResearchCollaborator[],
  uuid: string,
  email?: string
): boolean {
  const self = findSelf(collaborators, uuid, email);
  // Admin OR creator. The live case is both at once — access 'admin' with
  // role 'creator' — and either alone is enough.
  return !!self && (self.access === 'admin' || isOwner(self));
}

/**
 * The rows the shared control is given: everyone EXCEPT the current user.
 *
 * A user can never change their own access, so their row has no editable
 * state — showing it inside an editable list is only an invitation to try.
 * The owner is surfaced separately, as a locked row.
 */
export function manageableRows(
  collaborators: ResearchCollaborator[],
  uuid: string,
  email?: string
): ResearchCollaborator[] {
  const self = findSelf(collaborators, uuid, email);
  if (!self) return collaborators;
  return collaborators.filter((c) => c !== self);
}

export interface ShareDiff {
  added: ResearchCollaborator[];
  changed: ResearchCollaborator[];
  removed: ResearchCollaborator[];
}

/**
 * What actually changed. Diffing keeps a save idempotent — re-saving an
 * untouched list issues no writes at all. The owner is never in the diff.
 */
export function diffCollaborators(
  before: ResearchCollaborator[],
  after: ResearchCollaborator[]
): ShareDiff {
  const beforeByKey = new Map(before.map((c) => [collaboratorKey(c), c]));
  const afterByKey = new Map(after.map((c) => [collaboratorKey(c), c]));

  const added: ResearchCollaborator[] = [];
  const changed: ResearchCollaborator[] = [];
  after.forEach((collaborator) => {
    const key = collaboratorKey(collaborator);
    if (!key || isOwner(collaborator)) return;
    const existing = beforeByKey.get(key);
    if (!existing) added.push(collaborator);
    else if (existing.access !== collaborator.access) changed.push(collaborator);
  });

  const removed = before.filter((c) => {
    const key = collaboratorKey(c);
    return key && !isOwner(c) && !afterByKey.has(key);
  });

  return { added, changed, removed };
}

/* ------------------------------------------------------------------ */

interface Props {
  chatUuid: string;
  chatTitle: string;
  onShared?: () => void;
}

const ResearchShareModal: React.FC<Props> = ({ chatUuid, chatTitle, onShared }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userInfo = useAppSelector((s: RootState) => s.user?.userInfo);
  const selfUuid = currentUserUuid(userInfo);
  const selfEmail = String((userInfo as any)?.email || '');

  const [everyone, setEveryone] = useState<ResearchCollaborator[]>([]);
  const [others, setOthers] = useState<ResearchCollaborator[]>([]);
  const [original, setOriginal] = useState<ResearchCollaborator[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    dispatch(researchListCollaborators(chatUuid))
      .then((list: any[]) => {
        if (cancelled) return;
        const rows = normalise(list);
        const manageable = manageableRows(rows, selfUuid, selfEmail);
        setEveryone(rows);
        setOthers(manageable);
        setOriginal(manageable);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [chatUuid, selfUuid, selfEmail, dispatch]);

  const owner = useMemo(() => everyone.find(isOwner) || null, [everyone]);
  const canManage = useMemo(
    () => canManageAccess(everyone, selfUuid, selfEmail),
    [everyone, selfUuid, selfEmail]
  );

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    const { added, changed, removed } = diffCollaborators(original, others);
    try {
      await Promise.all([
        ...added.map((c) =>
          dispatch(researchAddCollaborator(chatUuid, collaboratorKey(c), c.access))
        ),
        ...changed.map((c) =>
          dispatch(researchUpdateCollaboratorAccess(chatUuid, collaboratorKey(c), c.access))
        ),
        ...removed.map((c) => dispatch(researchRemoveCollaborator(chatUuid, collaboratorKey(c)))),
      ]);
      dispatch(showFeedbackModal({
        showModal: true, modalType: 'success', message: t('Access updated'), duration: 3000,
      }));
      onShared?.();
      dispatch(hideModal());
    } catch {
      dispatch(showFeedbackModal({
        showModal: true, modalType: 'error', message: t('Could not update access'), duration: 3000,
      }));
    } finally {
      setIsSaving(false);
    }
  }, [chatUuid, others, original, dispatch, onShared, t]);

  return (
    <div className="min-w-[500px] sm:w-full" data-testid="research-share">
      <div className="px-4">
        {isLoading ? (
          <Skeleton variant="list" rows={3} />
        ) : (
          <>
            {/* The owner: a single locked row. No access control, no remove —
                the owner cannot be downgraded or taken off their own chat. */}
            {owner && (
              <div
                data-testid="share-owner-row"
                className="flex items-center gap-2 px-2.5 py-2 mb-3 rounded-lg
                           bg-surface-sunken/70 border border-hairline-soft"
              >
                {/* The owner's real face. The initials disc this replaces was
                    the only avatar in the share modal that never looked for a
                    picture — the collaborator rows below it already do. */}
                <ProfileImage
                  profilePicture={normaliseAccessPerson(owner).avatar}
                  email={owner.email || null}
                  name={owner.name}
                  initials={owner.initials || (owner.name || '?').slice(0, 1).toUpperCase()}
                  size="w-7 h-7"
                  className="flex-shrink-0"
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-medium text-fg-strong truncate">
                    {owner.name || owner.email || t('Unknown')}
                  </span>
                  {owner.email && owner.name && (
                    <span className="block text-[10px] text-fg-muted truncate">{owner.email}</span>
                  )}
                </span>
                <span
                  data-testid="share-owner-badge"
                  className="px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0
                             bg-champagne/15 border border-champagne/40 text-fg-strong"
                >
                  {t('Owner')}
                </span>
              </div>
            )}

            <CollaboratorSelector
              collaborators={others as any}
              onCollaboratorsChange={setOthers as any}
              title={t('Share this research')}
              description={
                canManage
                  ? t('People you add can open this chat and its documents.')
                  : t('You can see who has access. Only an admin can change it.')
              }
              adminUuid={owner ? collaboratorKey(owner) : undefined}
              allowedPermissions={['view', 'edit', 'admin']}
              readOnly={!canManage}
            />
          </>
        )}
      </div>

      <div className="flex justify-end gap-2 px-4 py-3 mt-2 border-t border-hairline-soft">
        <Button variant="neutral" size="small" onClick={() => dispatch(hideModal())}>
          {canManage ? t('Cancel') : t('Close')}
        </Button>
        {canManage && (
          <Button
            variant="primary"
            size="small"
            onClick={handleSave}
            isLoading={isSaving}
            disabled={isLoading || isSaving}
            data-testid="research-share-save"
          >
            {t('Save')}
          </Button>
        )}
      </div>

      <span className="sr-only">{chatTitle}</span>
    </div>
  );
};

export default ResearchShareModal;
